import {
  Box,
  Card,
  CardContent,
  Divider as MuiDivider,
  Typography,
  Button,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import Form from "./Form";
import {
  elencoToponomie,
  getProvince,
  getTipologiaAzienda,
} from "../../auth/form-sign-up/utils/registerAPI";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../redux/slices/snackbar";
import Preventivo from "./preventivo";
import {
  deepTrimStrings,
  validateEmail,
  validatePIVA,
  validateTelefono,
} from "../../auth/form-sign-up/utils/validationFunctions";
import { API_BASE_URL } from "../../../config";

const Divider = styled(MuiDivider)(spacing);

const CVTDetails = () => {
  const [loadingPage, setLoadingPage] = useState(true);
  const { idUID } = useParams();
  const [form, setForm] = useState({
    formaGiuridica: "persona",
    tipoSocieta: 1,
    cognomeRagioneSociale: "",
    nome: "",
    cf: "",
    piva: "",
    residenzaSedeLegale: {
      provincia: "",
      comune: "",
      cap: "",
      viacorso: "",
      indirizzo: "",
      civico: "",
    },
    email: "",
    cellulare: "",
    targa: "",
    filtri: {
      satellitare: 0,
      valoreVeicolo: "10000",
      durata: 12,
      provincia: "",
    },
    prodotto: "",
  });

  const isFormValid = () => {
    const {
      formaGiuridica,
      cognomeRagioneSociale,
      nome,
      piva,
      cf,
      email,
      cellulare,
      targa,
      residenzaSedeLegale,
    } = form;

    const validateTarga = () => {
      // Simple Italian plate validation: 2 letters + 3 digits + 2 letters (e.g., AA123BB)
      return /^[A-Z]{2}\d{3}[A-Z]{2}$/i.test(targa.trim());
    };

    const isBaseValid =
      cognomeRagioneSociale.trim() !== "" &&
      validateEmail(email) &&
      validatePIVA(piva) &&
      validateTelefono(cellulare) &&
      validateTarga(targa) &&
      residenzaSedeLegale.provincia !== "" &&
      residenzaSedeLegale.comune !== "" &&
      residenzaSedeLegale.cap !== "" &&
      residenzaSedeLegale.viacorso !== "" &&
      residenzaSedeLegale.indirizzo.trim() !== "" &&
      residenzaSedeLegale.civico.trim() !== "";

    if (formaGiuridica === "persona") {
      return (
        cognomeRagioneSociale.trim() !== "" &&
        validateEmail(email) &&
        nome.trim() !== "" &&
        validateTelefono(cellulare) &&
        validateTarga(targa) &&
        residenzaSedeLegale.provincia !== "" &&
        residenzaSedeLegale.comune !== "" &&
        residenzaSedeLegale.cap !== "" &&
        residenzaSedeLegale.viacorso !== "" &&
        residenzaSedeLegale.indirizzo.trim() !== "" &&
        residenzaSedeLegale.civico.trim() !== ""
      );
    }

    return isBaseValid;
  };
  const [activeStep, setActiveStep] = useState(0);
  const [province, setProvince] = useState([]);
  const [toponomie, setToponomie] = useState([]);
  const [tipologiaAziende, setTipologiaAziende] = useState([]);
  const [preventivo, setPreventivo] = useState("");

  const steps = ["Preventivo", "Dati Cliente"];

  const navigate = useNavigate();

  const dispatchSnackbar = useDispatch();

  // Snackbar helper
  const handleSnackbar = (message, severity) => {
    dispatchSnackbar(showSnackbar({ message, severity }));
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setPreventivo("");
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleSend = () => {
    const trimForm = deepTrimStrings(form);
    cvtCoveaSave(trimForm);
  };

  async function cvtCoveaSave(trimForm) {
    const token = sessionStorage.getItem("token");
    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          token,
          data: trimForm,
        }),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/cvtCoveaSave`,
        requestOptions
      );

      const responseData = await response.json();
      if (responseData.status === -1) {
        navigate("/auth/sign-in");
      }
      if (responseData.status === 0) {
        handleSnackbar(responseData.message, "error");
      }
      if (responseData.status === 1) {
        handleSnackbar(responseData.message, "success");
        navigate("/pages/cvt");
      }
    } catch (error) {
      console.log(error);
      return error;
    }
  }

  const handleReset = () => {
    setActiveStep(0);
  };

  useEffect(() => {
    getTipologiaAzienda().then((response) => {
      setTipologiaAziende(response.data);
    });
    getProvince().then((response) => {
      setProvince(response.data);
    });
    elencoToponomie()
      .then((response) => setToponomie(response.data))
      .finally(() => setLoadingPage(false));
  }, []);

  useEffect(() => {
    if (idUID !== undefined) {
      setActiveStep(1);
    }
  }, [idUID]);

  useEffect(() => {
    setForm({ ...form, prodotto: preventivo });
  }, [preventivo]);

  return (
    <Fragment>
      {loadingPage ? (
        <Box
          sx={{
            width: "calc(100vw - 40px)",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Fragment>
          <Helmet title="CVT" />
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h3" display="inline">
              CVT Details
            </Typography>
          </Box>
          <Divider my={4} />
          <Card sx={{ marginBottom: "16px", padding: "8px" }}>
            <CardContent>
              <Stepper activeStep={activeStep}>
                {steps.map((label, i) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              {activeStep === steps.length ? (
                <Fragment>
                  <Typography sx={{ mt: 2, mb: 1 }}>
                    All steps completed - you&apos;re finished
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                    <Box sx={{ flex: "1 1 auto" }} />
                    <Button onClick={handleReset}>Reset</Button>
                  </Box>
                </Fragment>
              ) : (
                <Box m={8}>
                  {activeStep === 0 ? (
                    <Preventivo
                      setForm={setForm}
                      form={form}
                      province={province}
                      setPreventivo={setPreventivo}
                    />
                  ) : (
                    <Form
                      setForm={setForm}
                      form={form}
                      province={province}
                      toponomie={toponomie}
                      TipologiaAziende={tipologiaAziende}
                    />
                  )}

                  {/* <Box my={8}>Calcolo preventivo</Box> */}

                  <Box sx={{ display: "flex", flexDirection: "row", pt: 4 }}>
                    <Button
                      color="inherit"
                      disabled={activeStep === 0}
                      onClick={handleBack}
                      sx={{ mr: 1 }}
                    >
                      Indietro
                    </Button>
                    <Box sx={{ flex: "1 1 auto" }} />

                    {activeStep === steps.length - 1 ? (
                      <Button
                        variant="contained"
                        onClick={handleSend}
                        disabled={!isFormValid()}
                      >
                        Invia
                      </Button>
                    ) : (
                      <Button disabled={!preventivo} onClick={handleNext}>
                        Avanti
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Fragment>
      )}
    </Fragment>
  );
};

export default CVTDetails;
